/* /account/profile - the student's details for applications (ProfileBundle). */
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { WEB_ROUTES, asLang, type Address, type EnglishInfo, type Lang, type Profile as ProfileT, type ProfileBundle } from '@pathabroad/core/browser';
import { useT } from '../app/i18n';
import { useMe } from '../app/session';
import { useErrorHandler, useLoad } from '../app/hooks';
import { Button, Checkbox, LoadError, Loading, PageHeader, Section, SelectField, TextField, type Option } from '../app/ui';
import { DateSelects } from './Start';
import { api, ApiError } from '../lib/api';
import { countryOptions, languageOptions, languageName, monthNames } from '../lib/format';
import { splitISODate, toISODate } from '../lib/forms';
import { buildPayload, newKey, schoolsMissingName, SENSITIVE, startTerms, toDraft, type Draft, type SensitiveKey } from '../lib/profile';
import { LANG_OPTIONS, type MessageKey } from '../lib/i18n';

const GENDERS = ['female', 'male', 'another', 'prefer_not_to_say'] as const;
const GENDER_KEYS: Record<typeof GENDERS[number], MessageKey> = { female: 'gender.female', male: 'gender.male', another: 'gender.another', prefer_not_to_say: 'gender.none' };
const LEVELS: Array<[string, MessageKey]> = [['foundation', 'level.foundation'], ['diploma', 'level.diploma'], ['bachelor', 'level.bachelor'], ['master', 'level.master'], ['phd', 'level.phd']];
const RELATIONS: Array<[string, MessageKey]> = [['mother', 'rel.mother'], ['father', 'rel.father'], ['guardian', 'rel.guardian'], ['other', 'rel.other']];
const EDUCATION: Array<[string, MessageKey]> = [['none', 'edu.none'], ['primary', 'edu.primary'], ['secondary', 'edu.secondary'], ['some_university', 'edu.some_university'],
  ['bachelor', 'edu.bachelor'], ['master', 'edu.master'], ['doctorate', 'edu.doctorate'], ['unknown', 'edu.unknown']];
/* test names are brands and stay as they are in both languages */
const TESTS: Array<[string, string]> = [['ielts', 'IELTS Academic'], ['toefl', 'TOEFL iBT'], ['duolingo', 'Duolingo English Test'], ['pte', 'PTE Academic'], ['cambridge', 'Cambridge English (C1/C2)']];
const INCOME: Array<[string, MessageKey]> = [['lt10k', 'income.lt10k'], ['10k_25k', 'income.10k_25k'], ['25k_50k', 'income.25k_50k'], ['50k_100k', 'income.50k_100k'], ['gt100k', 'income.gt100k']];
const CONSENT_KEYS: Record<SensitiveKey, MessageKey> = { race: 'profile.consent.race', religion: 'profile.consent.religion', family_income: 'profile.consent.family_income' };
const FIELD_KEYS: Record<SensitiveKey, MessageKey> = { race: 'f.race', religion: 'f.religion', family_income: 'f.family_income' };
const MAX_FAMILY = 4, MAX_SCHOOLS = 6;

/* keep a stored value selectable even if it isn't in our list */
const withCurrent = (opts: Option[], value: string | undefined, label?: string): Option[] =>
  value && !opts.some(o => o.value === value) ? [{ value, label: label ?? value }, ...opts] : opts;

export function Profile() {
  const { t } = useT();
  const loaded = useLoad(s => api.get<ProfileBundle>('/api/profile', { signal: s }), []);
  return (
    <>
      <PageHeader title={t('profile.title')} lead={t('profile.lead')} back={{ to: WEB_ROUTES.account, label: t('nav.account') }} />
      {loaded.status === 'loading' ? <Loading /> : loaded.status === 'error' ? <LoadError message={loaded.message} retry={loaded.reload} /> :
        <ProfileForm initial={loaded.data} />}
    </>
  );
}

function ProfileForm({ initial }: { initial: ProfileBundle }) {
  const { t, lang } = useT();
  const me = useMe();
  const minor = me.age === 'minor';
  const handle = useErrorHandler();
  const [draft, setDraft] = useState<Draft>(() => toDraft(initial));
  const [saved, setSaved] = useState<Draft>(() => toDraft(initial));
  const [dob, setDob] = useState(() => splitISODate(initial.profile?.dob));
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const p = draft.profile;
  const touch = () => { setDirty(true); setStatus(null); };
  const set = (patch: Partial<ProfileT>) => { setDraft(d => ({ ...d, profile: { ...d.profile, ...patch } })); touch(); };
  const setAddr = (patch: Partial<Address>) => set({ address: { ...p.address, ...patch } });
  const setEng = (patch: Partial<EnglishInfo>) => set({ english: { ...p.english, ...patch } });

  /* leaving with unsaved changes asks first */
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const countries = useMemo(() => countryOptions(lang).map(c => ({ value: c.code, label: c.name })), [lang]);
  const languages = useMemo(() => languageOptions(lang).map(l => ({ value: l.code, label: l.name })), [lang]);
  const months = monthNames(lang);
  const terms = useMemo(() => startTerms().map(x => ({ value: x.value, label: t('term.label', { month: months[x.month - 1], year: x.year }) })), [lang]); // eslint-disable-line react-hooks/exhaustive-deps
  const [c1, c2] = [p.citizenship?.[0] ?? '', p.citizenship?.[1] ?? ''];
  const setCitizenship = (a: string, b: string) => set({ citizenship: [a, b].filter((v, i, arr) => v && arr.indexOf(v) === i) });
  const consents = p.sensitive_consents ?? {};
  const setConsent = (k: SensitiveKey, on: boolean) => {
    /* unticking clears the detail straight away; saving then deletes it on the server */
    set({ sensitive_consents: { ...consents, [k]: on }, ...(on ? {} : { [k]: '' }) });
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const errs: Record<string, string> = {};
    const anyDob = dob.y || dob.m || dob.d;
    let dobISO: string | undefined = p.dob;
    if (anyDob) {
      if (!(dob.y && dob.m && dob.d)) errs.dob = t('dob.missing');
      else { const iso = toISODate(dob.y, dob.m, dob.d); if (!iso) errs.dob = t('dob.no_such_day'); else dobISO = iso; }
    }
    for (const i of schoolsMissingName(draft)) errs[`school-${i}`] = t('profile.schoolNameRequired');
    setErrors(errs);
    const firstBad = Object.keys(errs)[0];
    if (firstBad) {
      setStatus({ tone: 'error', text: t('err.invalid') });
      document.getElementById(firstBad === 'dob' ? 'pf-dob-day' : `pf-${firstBad}-name`)?.focus();
      return;
    }
    setBusy(true);
    setStatus(null);
    try {
      const payload = buildPayload({ ...draft, profile: { ...p, dob: dobISO } }, minor);
      const res = await api.put<ProfileBundle>('/api/profile', payload);
      const next = toDraft(res ?? payload);
      setDraft(next);
      setSaved(next);
      setDob(splitISODate(next.profile.dob));
      setDirty(false);
      setStatus({ tone: 'success', text: t('profile.saved') });
    } catch (er) {
      if (er instanceof ApiError && er.code === 'invalid' && er.field) {
        const id = `pf-${er.field.split('.').pop()}`;
        setErrors({ [er.field.split('.').pop()!]: t('err.invalid') });
        setStatus({ tone: 'error', text: t('err.invalid') });
        document.getElementById(id)?.focus();
      } else {
        const msg = handle(er);
        if (msg) setStatus({ tone: 'error', text: msg });
      }
    } finally { setBusy(false); }
  };

  const fieldErr = (k: string) => errors[k] ?? null;

  return (
    <form noValidate onSubmit={save} aria-labelledby="page-title" className="profile">
      <Group title={t('profile.sec.name')} id="sec-name" lead={t('profile.nameNote')}>
        <div className="grid2">
          <TextField id="pf-given_name" label={t('f.given_name')} autoComplete="given-name" value={p.given_name ?? ''} onChange={v => set({ given_name: v })} error={fieldErr('given_name')} />
          <TextField id="pf-middle_name" label={t('f.middle_name')} optional autoComplete="additional-name" value={p.middle_name ?? ''} onChange={v => set({ middle_name: v })} />
          <TextField id="pf-family_name" label={t('f.family_name')} autoComplete="family-name" value={p.family_name ?? ''} onChange={v => set({ family_name: v })} error={fieldErr('family_name')} />
          <TextField id="pf-preferred_name" label={t('f.preferred_name')} optional autoComplete="nickname" value={p.preferred_name ?? ''} onChange={v => set({ preferred_name: v })} />
        </div>
      </Group>

      <Group title={t('profile.sec.about')} id="sec-about">
        <fieldset className="fieldset">
          <legend className="label">{t('f.dob')}</legend>
          <DateSelects idPrefix="pf-dob" parts={dob} setParts={v => { setDob(v); touch(); }} error={!!errors.dob} />
          {errors.dob && <p className="field-error" role="alert">{errors.dob}</p>}
        </fieldset>
        <SelectField id="pf-gender" label={t('f.gender')} optional value={p.gender ?? ''} onChange={v => set({ gender: v })}
          options={withCurrent(GENDERS.map(g => ({ value: g, label: t(GENDER_KEYS[g]) })), p.gender)} />
      </Group>

      <Group title={t('profile.sec.citizenship')} id="sec-citizenship">
        <div className="grid2">
          <SelectField id="pf-citizenship" label={t('f.citizenship1')} value={c1} onChange={v => setCitizenship(v, v === c2 ? '' : c2)} options={countries} error={fieldErr('citizenship')} />
          <SelectField id="pf-citizenship2" label={t('f.citizenship2')} optional value={c2} onChange={v => setCitizenship(c1, v)}
            options={countries.filter(c => c.value !== c1)} disabled={!c1} />
          <SelectField id="pf-birth_country" label={t('f.birth_country')} value={p.birth_country ?? ''} onChange={v => set({ birth_country: v })} options={countries} />
          <SelectField id="pf-home_country" label={t('f.home_country')} value={p.home_country ?? ''} onChange={v => set({ home_country: v })} options={countries} hint={t('f.home_countryHint')} />
        </div>
        <SelectField id="pf-first_language" label={t('f.first_language')} value={p.first_language ?? ''} onChange={v => set({ first_language: v })}
          options={withCurrent(languages, p.first_language, languageName(p.first_language, lang))} />
      </Group>

      <Group title={t('profile.sec.contact')} id="sec-contact">
        <TextField id="pf-phone" label={t('f.phone')} type="tel" autoComplete="tel" inputMode="tel" ltr value={p.phone ?? ''} onChange={v => set({ phone: v })} hint={t('f.phoneHint')} error={fieldErr('phone')} />
        <TextField id="pf-line1" label={t('f.line1')} autoComplete="address-line1" value={p.address?.line1 ?? ''} onChange={v => setAddr({ line1: v })} />
        <TextField id="pf-line2" label={t('f.line2')} optional autoComplete="address-line2" value={p.address?.line2 ?? ''} onChange={v => setAddr({ line2: v })} />
        <div className="grid2">
          <TextField id="pf-city" label={t('f.city')} autoComplete="address-level2" value={p.address?.city ?? ''} onChange={v => setAddr({ city: v })} />
          <TextField id="pf-region" label={t('f.region')} optional autoComplete="address-level1" value={p.address?.region ?? ''} onChange={v => setAddr({ region: v })} />
          <TextField id="pf-postal_code" label={t('f.postal_code')} optional autoComplete="postal-code" ltr value={p.address?.postal_code ?? ''} onChange={v => setAddr({ postal_code: v })} />
          <SelectField id="pf-country" label={t('f.country')} value={p.address?.country ?? ''} onChange={v => setAddr({ country: v })} options={countries} autoComplete="country" />
        </div>
      </Group>

      <Group title={t('profile.sec.study')} id="sec-study">
        <div className="grid2">
          <SelectField id="pf-level" label={t('f.level')} value={p.level ?? ''} onChange={v => set({ level: v })}
            options={withCurrent(LEVELS.map(([v, k]) => ({ value: v, label: t(k) })), p.level)} />
          <TextField id="pf-field" label={t('f.field')} value={p.field ?? ''} onChange={v => set({ field: v })} />
          <SelectField id="pf-start_term" label={t('f.start_term')} value={p.start_term ?? ''} onChange={v => set({ start_term: v })} options={withCurrent(terms, p.start_term)} />
          <TextField id="pf-budget_usd" label={t('f.budget_usd')} hint={t('f.budgetHint')} type="number" inputMode="numeric" min={0} step={500} ltr
            value={p.budget_usd == null ? '' : String(p.budget_usd)}
            onChange={v => set({ budget_usd: v === '' ? null : Math.max(0, Math.round(Number(v)) || 0) })} />
        </div>
      </Group>

      <Group title={t('profile.sec.english')} id="sec-english">
        <Checkbox id="pf-english_first" checked={!!p.english?.first_language} onChange={v => setEng({ first_language: v })} label={t('f.english_first')} />
        <div className="grid2">
          <SelectField id="pf-english_type" label={t('f.english_type')} value={p.english?.type ?? ''} onChange={v => setEng({ type: v })}
            options={withCurrent([{ value: 'none', label: t('test.none') }, ...TESTS.map(([v, l]) => ({ value: v, label: l, lang: 'en' })), { value: 'other', label: t('test.other') }], p.english?.type)} />
          <TextField id="pf-english_score" label={t('f.english_score')} ltr value={p.english?.score ?? ''} onChange={v => setEng({ score: v })} />
          <TextField id="pf-english_date" label={t('f.english_date')} type="date" ltr value={p.english?.date ?? ''} onChange={v => setEng({ date: v })} />
        </div>
      </Group>

      <Group title={t('profile.sec.passport')} id="sec-passport">
        <TextField id="pf-passport_number" label={t('f.passport_number')} optional hint={t('f.passportHint')} autoComplete="off" ltr spellCheck={false}
          autoCapitalize="characters" value={p.passport_number ?? ''} onChange={v => set({ passport_number: v })} />
      </Group>

      <Group title={t('profile.sec.family')} id="sec-family" lead={t('profile.familyLead')}>
        {draft.family.map((m, i) => (
          <fieldset className="fieldset item" key={m._k}>
            <legend className="item__legend">{t('profile.familyItem', { n: i + 1 })}</legend>
            <div className="grid2">
              <TextField id={`pf-member-${i}-name`} label={t('f.member_name')} value={m.name ?? ''} onChange={v => editFamily(i, { name: v })} />
              <SelectField id={`pf-member-${i}-relation`} label={t('f.relation')} value={m.relation ?? ''} onChange={v => editFamily(i, { relation: v })}
                options={withCurrent(RELATIONS.map(([v, k]) => ({ value: v, label: t(k) })), m.relation)} />
              <SelectField id={`pf-member-${i}-education`} label={t('f.education')} value={m.education ?? ''} onChange={v => editFamily(i, { education: v })}
                options={withCurrent(EDUCATION.map(([v, k]) => ({ value: v, label: t(k) })), m.education)} />
              <TextField id={`pf-member-${i}-occupation`} label={t('f.occupation')} value={m.occupation ?? ''} onChange={v => editFamily(i, { occupation: v })} />
            </div>
            <Button variant="plain" small className="item__remove" aria-label={t('common.removeNamed', { name: m.name?.trim() || t('profile.familyItem', { n: i + 1 }) })}
              onClick={() => { setDraft(d => ({ ...d, family: d.family.filter((_, j) => j !== i) })); touch(); }}>{t('common.remove')}</Button>
          </fieldset>
        ))}
        {draft.family.length < MAX_FAMILY && (
          <Button variant="secondary" small onClick={() => { setDraft(d => ({ ...d, family: [...d.family, { _k: newKey() }] })); touch(); }}>{t('profile.familyAdd')}</Button>
        )}
      </Group>

      <Group title={t('profile.sec.schools')} id="sec-schools" lead={t('profile.schoolsLead')}>
        {draft.academics.map((a, i) => (
          <fieldset className="fieldset item" key={a._k}>
            <legend className="item__legend">{t('profile.schoolItem', { n: i + 1 })}</legend>
            <TextField id={`pf-school-${i}-name`} label={t('f.school_name')} value={a.school_name} onChange={v => editSchool(i, { school_name: v })} error={fieldErr(`school-${i}`)} />
            <div className="grid2">
              <TextField id={`pf-school-${i}-start`} label={t('f.start_date')} type="date" ltr value={a.start_date ?? ''} onChange={v => editSchool(i, { start_date: v })} />
              <TextField id={`pf-school-${i}-end`} label={t('f.end_date')} type="date" ltr value={a.end_date ?? ''} onChange={v => editSchool(i, { end_date: v })} />
              <TextField id={`pf-school-${i}-test`} label={t('f.test_type')} hint={t('f.test_typeHint')} value={a.test_type ?? ''} onChange={v => editSchool(i, { test_type: v })} />
              <TextField id={`pf-school-${i}-score`} label={t('f.test_score')} value={a.test_score ?? ''} onChange={v => editSchool(i, { test_score: v })} />
            </div>
            <Button variant="plain" small className="item__remove" aria-label={t('common.removeNamed', { name: a.school_name.trim() || t('profile.schoolItem', { n: i + 1 }) })}
              onClick={() => { setDraft(d => ({ ...d, academics: d.academics.filter((_, j) => j !== i) })); touch(); }}>{t('common.remove')}</Button>
          </fieldset>
        ))}
        {draft.academics.length < MAX_SCHOOLS && (
          <Button variant="secondary" small onClick={() => { setDraft(d => ({ ...d, academics: [...d.academics, { school_name: '', _k: newKey() }] })); touch(); }}>{t('profile.schoolAdd')}</Button>
        )}
      </Group>

      <Group title={t('profile.sec.sensitive')} id="sec-sensitive" lead={minor ? undefined : t('profile.sensitiveLead')}>
        {minor ? (
          <p className="muted" id="pf-sensitive-minor">{t('profile.sensitiveMinor')}</p>
        ) : SENSITIVE.map(k => (
          <div className="sensitive" key={k}>
            <Checkbox id={`pf-consent-${k}`} checked={!!consents[k]} onChange={v => setConsent(k, v)}
              label={t(CONSENT_KEYS[k])} description={t('profile.sensitivePurpose')} />
            {consents[k] ? (
              <div className="sensitive__field">
                {k === 'family_income'
                  ? <SelectField id="pf-family_income" label={t(FIELD_KEYS[k])} value={p.family_income ?? ''} onChange={v => set({ family_income: v })}
                    options={withCurrent(INCOME.map(([v, key]) => ({ value: v, label: t(key) })), p.family_income)} />
                  : <TextField id={`pf-${k}`} label={t(FIELD_KEYS[k])} value={(p[k] as string) ?? ''} onChange={v => set({ [k]: v })} />}
              </div>
            ) : saved.profile.sensitive_consents?.[k] ? <p className="hint sensitive__note">{t('profile.sensitiveCleared')}</p> : null}
          </div>
        ))}
      </Group>

      <Group title={t('profile.sec.lang')} id="sec-language">
        <SelectField id="pf-lang" label={t('f.lang')} value={p.lang ?? me.user.lang} placeholder={false} onChange={v => set({ lang: asLang(v) })}
          options={LANG_OPTIONS} />
      </Group>

      <div className="savebar">
        <Button type="submit" busy={busy}>{busy ? t('common.saving') : t('common.save')}</Button>
        <div role="status" aria-live="polite" className="savebar__status">
          {status ? <span className={status.tone === 'error' ? 'text-danger' : 'text-ok'}>{status.text}</span> : dirty ? t('profile.unsaved') : ''}
        </div>
      </div>
    </form>
  );

  function editFamily(i: number, patch: Record<string, string>) {
    setDraft(d => ({ ...d, family: d.family.map((m, j) => (j === i ? { ...m, ...patch } : m)) }));
    touch();
  }
  function editSchool(i: number, patch: Record<string, string>) {
    setDraft(d => ({ ...d, academics: d.academics.map((a, j) => (j === i ? { ...a, ...patch } : a)) }));
    touch();
  }
}

function Group({ title, id, lead, children }: { title: string; id: string; lead?: ReactNode; children: ReactNode }) {
  return (
    <Section title={title} id={id}>
      {lead && <p className="section__lead">{lead}</p>}
      <div className="card stack">{children}</div>
    </Section>
  );
}

